import React, { useState, useEffect } from "react";
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CreateTableData, PosTable } from "../../types/pos.types";

interface TableFormModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (tableData: CreateTableData) => void;
    initialData?: PosTable | null;
    existingAreas: string[];
    branchId: string;
    storeId: string | undefined;
}

export const TableFormModal: React.FC<TableFormModalProps> = ({
    open,
    onClose,
    onSave,
    initialData,
    existingAreas,
    branchId,
    storeId
}) => {
    const [name, setName] = useState(initialData?.table_name || "");
    const [number, setNumber] = useState(initialData?.table_number || "");
    const [capacity, setCapacity] = useState<number>(
        initialData?.capacity || 4
    );
    const [area, setArea] = useState<string>(initialData?.location.zone || "");

    // Local state for freeSolo options
    const [areaOptions, setAreaOptions] = useState<string[]>(existingAreas);

    useEffect(() => {
        setAreaOptions([...new Set([...existingAreas])]);
    }, [existingAreas]);

    const handleSave = () => {
        const tableData: CreateTableData = {
            table_id: initialData?.table_id || `table_${Date.now()}`, // generate ID if none exists
            table_name: name,
            table_number: number,
            capacity,
            status: "free", // default new table to free
            branch_id: branchId,
            store_id: storeId || undefined,
            location: {
                //   x: 0, // WE will update those with react flows in the FUTURE!!
                //   y: 0, // same as above
                zone: area,
            },
            is_active: true,
            is_deleted: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };

        onSave(tableData);
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 400,
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 3,
                    borderRadius: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography variant="h6">
                        {initialData ? "Edit Table" : "Add New Table"}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Autocomplete
                    freeSolo
                    value={area || ""}
                    onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                            setArea(newValue);
                        } else if (newValue && typeof newValue === "object" && "inputValue" in newValue) {
                            setArea(newValue as string);
                        } else {
                            setArea("");
                        }
                    }}
                    filterOptions={(options, params) => {
                        const filtered = options.filter((option) =>
                            option.toLowerCase().includes(params.inputValue.toLowerCase())
                        );

                        const isExisting = filtered.length > 0;

                        if (params.inputValue !== "" && !isExisting) {
                            filtered.push(`${params.inputValue}`);
                        }

                        return filtered;
                    }}
                    options={areaOptions}
                    getOptionLabel={(option) => {
                        return typeof option === "string" ? option : "";
                    }}
                    renderOption={(props, option) => {
                        return (
                            <li {...props}>
                                {option}
                            </li>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Area / Zone"
                            margin="normal"
                            fullWidth
                            onBlur={() => {
                                const inputValue = params.inputProps.value?.toString().trim();

                                if (!inputValue) return;

                                // Strip "Add " prefix before saving
                                const cleanValue = inputValue.startsWith('Add "')
                                    ? inputValue.slice(6, -1)
                                    : inputValue;

                                // Only add if not already present
                                if (!areaOptions.includes(cleanValue)) {
                                    setAreaOptions((prev) => [...prev, cleanValue]);
                                }

                                // Also update area state with clean value (optional)
                                if (area !== cleanValue) {
                                    setArea(cleanValue);
                                }
                            }}
                        />
                    )}
                />

                <TextField
                    label="Table Name"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    margin="normal"
                    required
                />

                <TextField
                    label="Table Number"
                    fullWidth
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    margin="normal"
                    required
                />

                <Autocomplete
                    freeSolo
                    options={[2, 4, 6, 8, 10]}
                    getOptionLabel={(option) => option.toString()}
                    value={capacity}
                    onChange={(event, newValue) => {
                        if (typeof newValue === "number") {
                            setCapacity(newValue);
                        } else if (typeof newValue === "string") {
                            const parsed = parseInt(newValue, 10);
                            if (!isNaN(parsed)) {
                                setCapacity(parsed);
                            }
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Capacity"
                            margin="normal"
                            fullWidth
                            type="number"
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "") {
                                    setCapacity(0); // or keep default as needed
                                } else {
                                    const num = parseInt(val, 10);
                                    if (!isNaN(num)) setCapacity(num);
                                }
                            }}
                            inputProps={{
                                ...params.inputProps,
                                min: 1,
                                placeholder: "Enter capacity",
                            }}
                        />
                    )}
                />

                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={onClose} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        disabled={!name.trim() || !number.trim() || !area.trim()}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};